import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FileDownload as ExportIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { api, ShareLinkAnalyticsQuery, ShareLinkAnalyticsResponse } from '../../../services/api';
import {
  DateRangePreset,
  DateRangeSelector,
  DateRangeState,
  DEFAULT_DATE_RANGE_PRESETS,
  addDaysDate,
  calendarToday,
  formatInputDate,
} from '../DateRangeSelector';
import {
  bucketSeries,
  downloadShareLinksWorkbook,
  formatInt,
  formatRangeShort,
  shareVizPalette,
  TrendView,
} from './shareLinksAnalyticsModel';
import ShareLinksKpiRow from './ShareLinksKpiRow';
import ShareLinksRankingSection from './ShareLinksRankingSection';
import ShareLinksTrendCard from './ShareLinksTrendCard';
import { overlineSx, segmentedToggleSx } from './shareLinksUi';

/** My Shareable Links went live in early October 2026; nothing to show before it. */
const SHARE_LINKS_LAUNCH_DATE = '2026-10-01';

const sinceLaunch = (): DateRangeState => ({
  startDate: SHARE_LINKS_LAUNCH_DATE,
  endDate: formatInputDate(calendarToday()),
});

const LAST_90_DAYS: DateRangePreset = {
  id: 'last90',
  label: 'Last 90 days',
  build: () => ({ startDate: formatInputDate(addDaysDate(calendarToday(), -89)), endDate: formatInputDate(calendarToday()) }),
};

const PRESETS: DateRangePreset[] = [
  { id: 'sinceLaunch', label: 'Since launch', build: sinceLaunch },
  ...DEFAULT_DATE_RANGE_PRESETS.flatMap((p): DateRangePreset[] => (p.id === 'last30' ? [p, LAST_90_DAYS] : [p])),
];

const VIEW_OPTIONS: Array<{ value: TrendView; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'cumulative', label: 'Cumulative' },
];

const SectionHeader: React.FC<{ title: string; caption: string }> = ({ title, caption }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, pt: 1, mb: '-8px !important' }}>
    <Typography variant="subtitle2" sx={{ ...overlineSx, fontSize: '0.72rem', color: 'var(--color-accent)' }}>{title}</Typography>
    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>{caption}</Typography>
  </Box>
);

const fetchCompanyWide = (query: ShareLinkAnalyticsQuery) => api.getAdminShareLinkAnalytics(query);

interface ShareLinksAnalyticsTabProps<T extends ShareLinkAnalyticsResponse> {
  /**
   * Loads the figures. Defaults to the company-wide admin endpoint; Statistics
   * passes a team- or project-scoped one. Keep it stable (useCallback) — a new
   * function is a new scope and refetches. Null while there is no scope to load
   * yet, e.g. before a project is chosen.
   */
  fetchAnalytics?: ((query: ShareLinkAnalyticsQuery) => Promise<T>) | null;
  /** Rendered first in the toolbar — the team or project picker. */
  toolbarStart?: React.ReactNode;
  /** Shown in place of the dashboard while `fetchAnalytics` is null. */
  emptyState?: React.ReactNode;
  /** One project in scope: drops the per-project tile and ranking, which would only ever show it. */
  singleProject?: boolean;
  /** Says whose figures these are, after the date-range caption. */
  scopeCaption?: (data: T) => string;
  /** Agents on the team in scope, for the share of them sharing. Team views only. */
  teamSize?: (data: T) => number;
  /** Names the scope in the Excel export's summary sheet and file name. */
  exportScope?: (data: T) => { label: string; fileTag: string };
}

function ShareLinksAnalyticsTab<T extends ShareLinkAnalyticsResponse = ShareLinkAnalyticsResponse>({
  fetchAnalytics = fetchCompanyWide as unknown as (query: ShareLinkAnalyticsQuery) => Promise<T>,
  toolbarStart,
  emptyState,
  singleProject = false,
  scopeCaption,
  teamSize,
  exportScope,
}: ShareLinksAnalyticsTabProps<T>): React.ReactElement {
  const theme = useTheme();
  const palette = shareVizPalette(theme.palette.mode);
  const tickColor = theme.palette.text.secondary;
  const inkColor = theme.palette.text.primary;

  const [range, setRange] = useState<DateRangeState>(sinceLaunch);
  const [view, setView] = useState<TrendView>('daily');
  const [excludeAgents, setExcludeAgents] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // Only the latest request may land, so a slow answer for the previous team or
  // project never overwrites the one just picked.
  const requestSeq = useRef(0);

  const load = useCallback(async (target: DateRangeState, excludePossibleAgents: boolean) => {
    const seq = ++requestSeq.current;
    if (!fetchAnalytics) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await fetchAnalytics({ ...target, excludePossibleAgents });
      if (seq === requestSeq.current) setData(next);
    } catch (err) {
      if (seq === requestSeq.current) setError(err instanceof Error ? err.message : 'Failed to load share link analytics');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [fetchAnalytics]);

  // A new scope starts blank rather than showing the previous one's figures dimmed.
  useEffect(() => {
    setData(null);
  }, [fetchAnalytics]);

  useEffect(() => {
    void load(range, excludeAgents);
  }, [load, range, excludeAgents]);

  const buckets = useMemo(() => (data ? bucketSeries(data.series, view) : []), [data, view]);
  const periodLabel = data ? formatRangeShort(data.previousRange.startDate, data.previousRange.endDate) : '';

  const handleExport = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await downloadShareLinksWorkbook(data, exportScope?.(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {toolbarStart}
        <DateRangeSelector range={range} onApply={setRange} presets={PRESETS} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, value: TrendView | null) => value && setView(value)}
          sx={{ ...segmentedToggleSx, height: 40 }}
          aria-label="Trend view"
        >
          {VIEW_OPTIONS.map((o) => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Tooltip
          arrow
          title="Leave out buyers whose mobile is listed as a property agent (a Huttons user or on the CEA register) from leads, unique mobiles and verifications."
        >
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={excludeAgents}
                onChange={(e) => setExcludeAgents(e.target.checked)}
                sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
              />
            )}
            label={(
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.88)', whiteSpace: 'nowrap' }}>
                Exclude possible agents
                {data ? (
                  <Box component="span" sx={{ ml: 0.75, color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                    {`(${formatInt(data.possibleAgentLeads)})`}
                  </Box>
                ) : null}
              </Typography>
            )}
            sx={{ m: 0 }}
          />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void load(range, excludeAgents)}
          disabled={loading || !fetchAnalytics}
          sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <ExportIcon />}
          onClick={() => void handleExport()}
          disabled={!data || exporting}
          sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
        >
          Export Excel
        </Button>
      </Box>

      {data ? (
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
          {scopeCaption ? `${scopeCaption(data)} · ` : ''}
          {`${formatRangeShort(data.range.startDate, data.range.endDate)} · changes compared with ${periodLabel}`}
          {data.excludePossibleAgents
            ? ` · ${formatInt(data.possibleAgentLeads)} ${data.possibleAgentLeads === 1 ? 'lead' : 'leads'} from possible agents left out`
            : ''}
          {data.uncheckedLeads > 0 ? ` · ${formatInt(data.uncheckedLeads)} not yet checked for agents` : ''}
        </Typography>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!fetchAnalytics ? (
        emptyState ?? null
      ) : !data ? (
        loading ? (
          <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : null
      ) : (
        <Box sx={{ position: 'relative' }}>
          {loading ? <LinearProgress sx={{ position: 'absolute', top: -10, left: 0, right: 0, height: 2 }} /> : null}
          {/* Refetches keep the previous render, dimmed, instead of flashing a spinner. */}
          <Stack spacing={2.5} sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 150ms ease' }}>
            <ShareLinksKpiRow
              data={data}
              palette={palette}
              periodLabel={periodLabel}
              hideProjects={singleProject}
              teamSize={teamSize?.(data)}
            />

            <SectionHeader title="Over time" caption="Links on the day they were shared; leads on the day the buyer verified." />
            <Grid container spacing={2}>
              <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
                <ShareLinksTrendCard
                  metric="links"
                  title="Links shared over time"
                  buckets={buckets}
                  view={view}
                  palette={palette}
                  tickColor={tickColor}
                  inkColor={inkColor}
                  fileName="share-links-shared"
                />
              </Grid>
              <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
                <ShareLinksTrendCard
                  metric="leads"
                  title="Leads received over time"
                  buckets={buckets}
                  view={view}
                  palette={palette}
                  tickColor={tickColor}
                  inkColor={inkColor}
                  fileName="share-links-leads"
                />
              </Grid>
            </Grid>

            <SectionHeader
              title="Who shares what"
              caption={singleProject
                ? 'Top agents sharing this project, ranked by links or by leads. Click a bar for the breakdown.'
                : 'Top projects and top agents, ranked by links or by leads. Click a bar for the breakdown.'}
            />
            <Grid container spacing={2} alignItems="stretch">
              {singleProject ? null : (
                <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
                  <ShareLinksRankingSection
                    kind="project"
                    title="Top 5 projects"
                    subtitle={`${formatInt(data.projects.filter((p) => p.links > 0).length)} projects shared in this period`}
                    topN={5}
                    data={data}
                    palette={palette}
                    tickColor={tickColor}
                    inkColor={inkColor}
                  />
                </Grid>
              )}
              <Grid item xs={12} lg={singleProject ? 12 : 6} sx={{ minWidth: 0 }}>
                <ShareLinksRankingSection
                  kind="agent"
                  title="Top 10 agents sharing"
                  subtitle={`${formatInt(data.totals.sharers)} agents shared links in this period`}
                  topN={10}
                  data={data}
                  palette={palette}
                  tickColor={tickColor}
                  inkColor={inkColor}
                />
              </Grid>
            </Grid>

            <Typography variant="caption" component="div" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', lineHeight: 1.7 }}>
              <strong>How these are counted.</strong> Links count on the day they were shared. Leads count on the day the
              buyer verified their mobile on WhatsApp and was filed into the agent&apos;s Super Connector, including leads on
              links shared before this period. An agent testing their own link is never a lead. Opens are only kept as an
              all-time total per link, so they are summed over the links shared in this period and have no daily breakdown.
              {' '}A possible agent is a buyer whose mobile matches a Huttons user or a salesperson on the CEA public register;
              excluding them changes the lead and verification figures only.
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

export default ShareLinksAnalyticsTab;
